import { useEffect, useMemo, useRef, useState } from 'react'
import { SWRConfig as SWRConfigProvider } from 'swr'
import { COOKIE_NAME_JWT } from '../../constants'
import { useAuth } from '../../hooks/useAuth'
import { useApiRestClient } from '../api-rest-client/api-rest-client'
import { useCookiesByGroup } from '../../hooks/useCookiesByGroup'
import { useStore } from '../store'
import { bluheadlessSwrMiddleware } from '../../stale-while-revalidate'

const EMPTY_VALUE = {}

const SWRConfig = ({ children, value = EMPTY_VALUE }) => {
	const { code: storeCode } = useStore()

	const [{ [COOKIE_NAME_JWT]: cookieJwt }] = useCookiesByGroup([COOKIE_NAME_JWT])
	const [apiPaused, setApiPaused] = useState(!!storeCode && !cookieJwt)

	const client = useApiRestClient()
	const { authInit, refreshAuthCookies } = useAuth(client)
	const authInitProgress = useRef(false)

	useEffect(() => {
		const initAuth = async () => {
			if (!cookieJwt) {
				setApiPaused(true) // when logout cookieJwt is removed and api need to be paused
				if (!authInitProgress.current) {
					authInitProgress.current = true
					await authInit()
					authInitProgress.current = false
				}
			} else {
				setApiPaused(false)
			}
		}
		if (storeCode) {
			initAuth()
		}
	}, [cookieJwt, authInit, storeCode])

	useEffect(() => {
		// run on every render = every route change
		if (storeCode && cookieJwt && refreshAuthCookies) {
			refreshAuthCookies()
		}
	})

	const swrConfigValue = useMemo(
		() => ({
			__bh__apiPaused: apiPaused,
			errorRetryCount: 0,
			revalidateIfStale: false,
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			use: [bluheadlessSwrMiddleware],
			...value,
		}),
		[apiPaused, value]
	)

	return <SWRConfigProvider value={swrConfigValue}>{children}</SWRConfigProvider>
}

export { SWRConfig }
